import { useEffect, useMemo } from "react";
import { groupNotifications } from "@/lib/notifications";
import { useNotifications } from "@/lib/useNotifications";
import { Case } from "@/lib/useRealtimeCases";
import { useSelectedCenters } from "@/lib/SelectedCentersContext";
import { SlidePanel } from "@/components/ui/SlidePanel";
import { MultiSelectDropdown } from "@/components/ui/MultiSelectDropdown";

interface MessagesSlidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onCaseClick: (caseId: string) => void;
  cases: Case[];
}

export function MessagesSlidePanel({
  isOpen,
  onClose,
  onCaseClick,
  cases,
}: MessagesSlidePanelProps) {
  // ── Centres disponibles ──────────────────────────────────────────────
  const availableCenters = useMemo(() => {
    const unique = Array.from(
      new Set(cases.map((c) => c.center).filter(Boolean))
    ) as string[];
    return unique.sort();
  }, [cases]);

  const { selectedCenters, setSelectedCenters } = useSelectedCenters();

  // Si aucun centre sélectionné et des centres disponibles, tout sélectionner
  useEffect(() => {
    if (availableCenters.length > 0 && selectedCenters.length === 0) {
      setSelectedCenters(availableCenters);
    }
  }, [availableCenters, selectedCenters.length, setSelectedCenters]);

  // ── Notifications ────────────────────────────────────────────────────
  const {
    notifications,
    loading,
    error,
    markCaseNotificationsRead,
  } = useNotifications();

  const { messageGroups } = useMemo(
    () => groupNotifications(notifications, selectedCenters),
    [notifications, selectedCenters]
  );

  const totalMessages = messageGroups.reduce((a, g) => a + g.unreadCount, 0);

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleCaseClick = (caseId: string) => {
    onCaseClick(caseId);
    onClose();
  };

  const handleMarkRead = async (e: React.MouseEvent, caseId: string) => {
    e.stopPropagation();
    await markCaseNotificationsRead(caseId, "message");
  };

  // ── Rendu ────────────────────────────────────────────────────────────
  return (
    <SlidePanel isOpen={isOpen} onClose={onClose}>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="px-6 py-5 border-b border-red-100 bg-gradient-to-r from-red-50 via-orange-50/60 to-white flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-red-100 flex items-center justify-center shadow-sm">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Nouveaux messages
              </h2>
              <p className="text-sm text-gray-500">
                {totalMessages > 0
                  ? `${totalMessages} message${totalMessages > 1 ? "s" : ""} en attente de réponse`
                  : "Aucun message en attente"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <MultiSelectDropdown
          label="Centres"
          value={selectedCenters}
          onChange={setSelectedCenters}
          options={availableCenters}
          placeholder="Filtrer par centre..."
          allowCustom={false}
        />
      </div>

      {/* ── Contenu ───────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <EmptyState
            icon="⏳"
            title="Chargement..."
          />
        ) : error ? (
          <EmptyState
            icon="⚠️"
            title="Impossible de charger"
            subtitle={error}
          />
        ) : messageGroups.length === 0 ? (
          <EmptyState
            icon="✅"
            title="Aucun message en attente"
            subtitle="Tous les messages ont été traités"
          />
        ) : (
          <div className="p-4 space-y-3">
            {messageGroups.map((group) => (
              <div
                key={group.caseId}
                onClick={() => handleCaseClick(group.caseId)}
                className="group relative bg-white border border-gray-200 rounded-xl p-4 hover:border-red-200 hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  {/* Indicateur */}
                  <div className="flex-shrink-0 mt-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                  </div>

                  {/* Contenu principal */}
                  <div className="flex-1 min-w-0">
                    {/* Ligne 1 : Entreprise + temps */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h4 className="text-sm font-bold text-gray-900 truncate">
                        {group.companyName || group.clientName}
                      </h4>
                      <span className="text-xs text-gray-400 flex-shrink-0 tabular-nums">
                        {formatRelativeTime(group.lastMessageAt)}
                      </span>
                    </div>

                    {/* Ligne 2 : Client + Titre */}
                    <p className="text-sm text-gray-600 truncate mb-1.5">
                      {group.companyName
                        ? `${group.clientName} · ${group.caseTitle}`
                        : group.caseTitle}
                    </p>

                    {/* Ligne 3 : Badges */}
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center text-xs font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
                        {group.unreadCount} message{group.unreadCount > 1 ? "s" : ""}
                      </span>
                      {group.center && (
                        <span className="inline-flex items-center text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                          {group.center}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Bouton marquer lu */}
                  <button
                    onClick={(e) => handleMarkRead(e, group.caseId)}
                    className="flex-shrink-0 p-2 rounded-lg text-gray-300 hover:text-green-600 hover:bg-green-50 opacity-0 group-hover:opacity-100 transition-all"
                    title="Marquer comme lu"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SlidePanel>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

function EmptyState({
  icon,
  title,
  subtitle,
}: {
  icon: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
      <div className="text-5xl mb-4">{icon}</div>
      <p className="text-base font-semibold text-gray-900 mb-1">{title}</p>
      {subtitle && <p className="text-sm text-gray-500 max-w-xs">{subtitle}</p>}
    </div>
  );
}

function formatRelativeTime(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `${diffMins} min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}j`;

    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
    });
  } catch {
    return dateStr;
  }
}
